import { Link } from "react-router-dom";
import { Radio, Bookmark, LogIn, User, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getFirstName } from "@/lib/getFirstName";
import { usePrefetchOnIntent } from "@/hooks/usePrefetchOnIntent";

interface HeaderProps {
  compact?: boolean;
}

export default function Header({ compact = false }: HeaderProps) {
  const { user, profile } = useAuth();
  // Warm Today's lazy route chunk on hover/touch so the click feels instant.
  // Saved and Login are eager-bundled with the entry, no prefetch needed.
  const prefetchToday = usePrefetchOnIntent(() => import("@/pages/Today"));

  return (
    <header className="w-full border-b-2 border-border bg-background">
      <div className="container px-4 sm:px-6 flex items-center justify-between py-2.5 sm:py-3">
        <Link to="/" className="flex items-center gap-1.5 sm:gap-2 group">
          <Radio className="w-4 h-4 sm:w-5 sm:h-5 text-primary group-hover:text-retro-amber transition-colors" />
          <span className="font-mono font-bold text-sm sm:text-lg tracking-tight">
            AskThe<span className="text-primary">Pods</span>
          </span>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-3">
          <Link
            to="/today"
            aria-label="Today's pick"
            {...prefetchToday}
            className="flex items-center gap-1 text-[11px] sm:text-xs font-mono text-muted-foreground hover:text-foreground transition-colors p-2 sm:p-0 -m-1 sm:m-0"
            title="Today's pick"
          >
            <Sparkles className="w-5 h-5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Today</span>
          </Link>
          {user ? (
            <>
              <Link
                to="/saved"
                aria-label="Saved episodes"
                className="flex items-center gap-1 text-[11px] sm:text-xs font-mono text-muted-foreground hover:text-foreground transition-colors p-2 sm:p-0 -m-1 sm:m-0"
                title="Saved episodes"
              >
                <Bookmark className="w-5 h-5 sm:w-4 sm:h-4" />
                <span className="inline">Saved</span>
              </Link>
              <Link to="/saved" className="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-0" title="My Library" aria-label="My Library">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt=""
                    className="w-8 h-8 sm:w-7 sm:h-7 rounded-full border-2 border-border object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 sm:w-7 sm:h-7 rounded-full border-2 border-border bg-muted flex items-center justify-center">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
                <span className="hidden sm:inline font-mono text-xs text-muted-foreground truncate max-w-[100px]">
                  {getFirstName(profile?.display_name, user.email?.split("@")[0] || "")}
                </span>
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/saved"
                aria-label="Saved episodes"
                className="flex items-center gap-1 text-[11px] sm:text-xs font-mono text-muted-foreground hover:text-foreground transition-colors p-2 sm:p-0 -m-1 sm:m-0"
                title="Saved episodes"
              >
                <Bookmark className="w-5 h-5 sm:w-4 sm:h-4" />
                <span className="inline">Saved</span>
              </Link>
              <Link
                to="/login"
                className="btn-comic gap-1 text-[11px] sm:text-xs px-3 py-2 sm:px-3 sm:py-1.5 min-h-[40px] sm:min-h-0 bg-secondary text-secondary-foreground border-foreground"
              >
                <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4 relative z-10" /> <span className="relative z-10">Sign In</span>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
